import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import { getUserTimezone } from '../shared/utils/date.util';

export interface SalesStatistics {
  total_revenue_today: string;
  washes_today: string;
  total_revenue_all_time: string;
  washes_till_today: string;
}

export interface SalesStatisticsResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: SalesStatistics;
}

/**
* Angular injectable service that fetches, normalizes, and exposes sales statistics from the backend API.
* This service is provided in root and is responsible for requesting attendant sales statistics, applying
* timezone context, and converting the ApiResponse shape into a stable SalesStatisticsResponse format for consumers.
* Key method:
*   - getSalesStatistics(): Returns an Observable<SalesStatisticsResponse> that emits a normalized response object.
* No Angular component lifecycle hooks apply to this service; it is instantiated via dependency injection.
* @component
* @selector N/A
* @example
*   // Inject and use in a component or another service
*   constructor(private salesService: SalesService) {}
*   this.salesService.getSalesStatistics().subscribe(response => { /* handle stats */
@Injectable({
  providedIn: 'root'
})
export class SalesService {
  constructor(private apiService: ApiService) { }

  /**
   * Fetch sales statistics from the API
   * @returns Observable<SalesStatisticsResponse>
   */
  getSalesStatistics(): Observable<SalesStatisticsResponse> {
    const timezone = getUserTimezone();
    return this.apiService.get<SalesStatistics>('attendant/statistics/sales', { timezone }).pipe(
      map((response: ApiResponse<SalesStatistics>) => {
        // Convert ApiResponse to SalesStatisticsResponse format for backward compatibility
        return {
          status: response.status,
          status_code: response.status ? 200 : 400,
          message: response.message || '',
          data: response.data || {
            total_revenue_today: '0.00',
            washes_today: '0',
            total_revenue_all_time: '0.00',
            washes_till_today: '0'
          }
        } as SalesStatisticsResponse;
      })
    );
  }
}
